"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { BRAND_NAMES, LOJAS_DATA } from "@/app/utils/utils";
import { pushLeadForm } from "@/app/utils/gtm";

type SacContactModalProps = {
	onClose: () => void;
};

type FormState =
	| { status: "idle" }
	| { status: "loading" }
	| { status: "success"; message: string }
	| { status: "error"; message: string };

const SAC_ASSUNTOS = [
	{ value: "1", label: "Venda" },
	{ value: "2", label: "Pós-venda" },
	{ value: "4", label: "Serviço" },
	{ value: "5", label: "Outro" },
] as const;

const selectClassName =
	"w-full border-b border-neutral-400 py-2 outline-none bg-transparent text-neutral-800 focus:border-neutral-900";

function digitsOnly(value: string) {
	return value.replace(/\D/g, "");
}

function maskTel(d: string) {
	let v = d.replace(/\D/g, "").slice(0, 11);
	v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
	v = v.replace(/(\d)(\d{4})$/, "$1-$2");
	return v.slice(0, 15);
}

function isFullName(value: string): boolean {
	const parts = value.trim().split(/\s+/).filter(Boolean);
	return parts.length >= 2;
}

const SAC_SUBJECT_DEFAULT = "Novo contato via SAC (site Toyota - Osaka)";

const SAC_MAIL_SUBJECT =
	process.env.NEXT_PUBLIC_SAC_EMAIL_SUBJECT?.trim() || SAC_SUBJECT_DEFAULT;

function buildSacIntegrationDescription(
	linhaIntro: string,
	nomeCompleto: string,
	email: string,
	telefoneFormatado: string,
	assuntoSac: string,
	loja: string,
	mensagemUsuario: string,
): string {
	return [
		linhaIntro,
		"",
		`Nome completo: ${nomeCompleto}`,
		`E-mail: ${email}`,
		`Telefone: ${telefoneFormatado}`,
		`Assunto: ${assuntoSac}`,
		`Loja: ${loja}`,
		"",
		"Descrição:",
		mensagemUsuario,
	].join("\n");
}

export default function SacContactModal({ onClose }: SacContactModalProps) {
	const [nomeCompleto, setNomeCompleto] = useState("");
	const [email, setEmail] = useState("");
	const [telefone, setTelefone] = useState("");
	const [assuntoSac, setAssuntoSac] = useState("");
	const [loja, setLoja] = useState("");
	const [descricao, setDescricao] = useState("");
	const [state, setState] = useState<FormState>({ status: "idle" });

	const canSubmit = useMemo(() => {
		if (!nomeCompleto.trim() || !isFullName(nomeCompleto)) return false;
		if (!email.trim()) return false;
		const tel = digitsOnly(telefone);
		if (tel.length < 10 || tel.length > 11) return false;
		if (!assuntoSac) return false;
		if (!loja) return false;
		if (!descricao.trim()) return false;
		return true;
	}, [nomeCompleto, email, telefone, assuntoSac, loja, descricao]);

	const onKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		},
		[onClose]
	);

	useEffect(() => {
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onKeyDown]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;

		setState({ status: "loading" });

		try {
			const telefoneFmt = maskTel(telefone).trim() || telefone.trim();
			const emailTrim = email.trim();
			const assuntoLabel =
				SAC_ASSUNTOS.find((item) => item.value === assuntoSac)?.label ||
				assuntoSac;

			const payload = {
				email: emailTrim,
				descricao: buildSacIntegrationDescription(
					SAC_MAIL_SUBJECT,
					nomeCompleto.trim(),
					emailTrim,
					telefoneFmt,
					assuntoLabel,
					loja,
					descricao.trim(),
				),
				telefone: digitsOnly(telefone),
				subject: SAC_MAIL_SUBJECT,
			};

			const resp = await fetch("/api/sac-contato", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			const data = (await resp.json().catch(() => ({}))) as {
				ok?: boolean;
				message?: string;
				error?: string;
			};

			if (!resp.ok) {
				throw new Error(data?.error || "Não foi possível enviar sua mensagem.");
			}

			pushLeadForm();

			setState({
				status: "success",
				message:
					(typeof data?.message === "string" && data.message) ||
					"Mensagem enviada com sucesso.",
			});
			setNomeCompleto("");
			setEmail("");
			setTelefone("");
			setAssuntoSac("");
			setLoja("");
			setDescricao("");
		} catch (err) {
			setState({
				status: "error",
				message: err instanceof Error ? err.message : "Erro ao enviar.",
			});
		}
	};

	return (
		<>
			<button
				type="button"
				aria-label="Fechar modal"
				onClick={onClose}
				className="bg-black/50 backdrop-blur-sm w-screen h-screen fixed z-50 top-0 right-0"
			/>

			<div className="fixed w-[92vw] max-w-[620px] bg-white rounded-2xl z-[51] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-[0_12px_40px_rgba(0,0,0,0.30)] overflow-hidden max-h-[90vh]">
				<div className="px-8 pt-7 pb-3 flex items-start justify-between">
					<div>
						<h2 className="text-3xl font-semibold text-neutral-900">
							Queremos te ouvir
						</h2>
						<p className="mt-2 text-sm text-neutral-600 max-w-[520px]">
							Seja uma dúvida, sugestão ou problema, conte pra gente. Sua
							mensagem é importante.
						</p>
					</div>
					<button
						type="button"
						aria-label="Fechar"
						onClick={onClose}
						className="text-neutral-700 hover:text-neutral-900"
					>
						<AiOutlineClose size={22} />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="px-8 pb-8 pt-2 overflow-auto" noValidate>
					<div className="flex flex-col gap-6">
						<label className="flex flex-col gap-2">
							
							<input
								value={nomeCompleto}
								onChange={(e) => setNomeCompleto(e.target.value)}
								type="text"
								required
								autoComplete="name"
								placeholder="Nome completo*"
								className="w-full border-b border-neutral-400 py-2 outline-none focus:border-neutral-900"
							/>
						</label>

						<label className="flex flex-col gap-2">
							
							<input
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								type="email"
								required
								autoComplete="email"
								placeholder="E-mail*"
								className="w-full border-b border-neutral-400 py-2 outline-none focus:border-neutral-900"
							/>
						</label>

						<label className="flex flex-col gap-2">
							<input
								value={telefone}
								onChange={(e) => setTelefone(maskTel(e.target.value))}
								type="tel"
								inputMode="tel"
								required
								autoComplete="tel"
								placeholder="Telefone*"
								className="w-full border-b border-neutral-400 py-2 outline-none focus:border-neutral-900"
							/>
						</label>

						<label className="flex flex-col gap-2">
							<select
								value={assuntoSac}
								onChange={(e) => setAssuntoSac(e.target.value)}
								required
								className={`${selectClassName} pl-2`}
							>
								<option value="" disabled>
									Assunto*
								</option>
								{SAC_ASSUNTOS.map((item) => (
									<option key={item.value} value={item.value}>
										{item.label}
									</option>
								))}
							</select>
						</label>

						<label className="flex flex-col gap-2">
							<select
								value={loja}
								onChange={(e) => setLoja(e.target.value)}
								required
								className={`${selectClassName} pl-2`}
							>
								<option value="" disabled>
									Loja*
								</option>
								{LOJAS_DATA.map((item) => (
									<option key={item.nome} value={item.nome}>
										{`${BRAND_NAMES.DEFAULT} ${item.nome}`}
									</option>
								))}
							</select>
						</label>

						<label className="flex flex-col gap-2">
							<textarea
								value={descricao}
								onChange={(e) => setDescricao(e.target.value)}
								required
								rows={4}
								placeholder="Descrição da mensagem*"
								className="w-full border-b border-neutral-400 py-2 outline-none resize-none focus:border-neutral-900 text-neutral-800"
							/>
						</label>
					</div>

					<div className="mt-7">
						<button
							type="submit"
							disabled={!canSubmit || state.status === "loading"}
							className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold py-4 rounded-sm transition-colors"
						>
							{state.status === "loading" ? "ENVIANDO..." : "ENVIAR MENSAGEM"}
						</button>
					</div>

					{state.status === "success" && (
						<p className="mt-4 text-sm text-green-700">{state.message}</p>
					)}
					{state.status === "error" && (
						<p className="mt-4 text-sm text-red-700">{state.message}</p>
					)}
					
				</form>
			</div>
		</>
	);
}
